import { useState, useEffect, useCallback } from 'react';
import type { Agent, AgentWithRelations, CreateAgentRequest, UpdateAgentRequest } from '../types/agent.types';
import * as agentApi from '../api/agents';

export function useAgents(workspaceId: string | null, autoLoad = true) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadAgents = useCallback(async (params?: { search?: string; limit?: number; offset?: number }) => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await agentApi.fetchAgents(workspaceId, params);
      setAgents(result.agents);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to load agents:', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const createAgent = useCallback(async (data: CreateAgentRequest): Promise<Agent> => {
    if (!workspaceId) throw new Error('No workspace');
    setError(null);
    try {
      const agent = await agentApi.createAgent(workspaceId, data);
      setAgents((prev) => [agent, ...prev]);
      return agent;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [workspaceId]);

  const updateAgent = useCallback(async (id: string, data: UpdateAgentRequest): Promise<Agent> => {
    if (!workspaceId) throw new Error('No workspace');
    setError(null);
    try {
      const agent = await agentApi.updateAgent(workspaceId, id, data);
      setAgents((prev) => prev.map((a) => (a.id === id ? agent : a)));
      return agent;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [workspaceId]);

  const deleteAgent = useCallback(async (id: string): Promise<void> => {
    if (!workspaceId) throw new Error('No workspace');
    setError(null);
    try {
      await agentApi.deleteAgent(workspaceId, id);
      setAgents((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [workspaceId]);

  useEffect(() => {
    if (autoLoad && workspaceId) {
      loadAgents();
    }
  }, [autoLoad, workspaceId, loadAgents]);

  return {
    agents,
    loading,
    error,
    loadAgents,
    createAgent,
    updateAgent,
    deleteAgent,
  };
}

export function useAgent(workspaceId: string | null, agentId: string | null) {
  const [agent, setAgent] = useState<AgentWithRelations | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadAgent = useCallback(async () => {
    if (!agentId || !workspaceId) return;

    setLoading(true);
    setError(null);
    try {
      const result = await agentApi.fetchAgent(workspaceId, agentId);
      setAgent(result);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to load agent:', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, agentId]);

  useEffect(() => {
    if (agentId && workspaceId) {
      loadAgent();
    }
  }, [agentId, workspaceId, loadAgent]);

  return {
    agent,
    loading,
    error,
    loadAgent,
  };
}
